// src/App.jsx
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

// Common Components
import HomePage from "./HomePage";
import Dashboard from "./Dashboard";
import DirectMessages from "./common/DirectMessages";
import JitsiRoom from "./common/JitsiRoom";

// Pages
import PublicBookingPage from "./pages/PublicBookingPage";
import BookSessionPage from "./pages/BookSessionPage";
import About from "./pages/Myversion/About.jsx";
import Services from "./pages/Myversion/Services.jsx";
import Contact from "./pages/Myversion/Contact.jsx";

// Expert Components
import ExpertDashboard from "./expert/ExpertDashboard";
import ExpertHome from "./expert/ExpertHome";
import ExpertBookings from "./expert/ExpertBookings";
import ExpertPriorityDM from "./expert/ExpertPriorityDM";
import ExpertTestimonials from "./expert/ExpertTestimonials";
import ExpertCalendar from "./expert/Calendar";
import ExpertServices from "./expert/ExpertServices";
import ExpertAnalytics from "./expert/ExpertAnalytics";
import ExpertMarketing from "./expert/ExpertMarketing";
import ExpertPayments from "./expert/ExpertPayments";
import ExpertCheckout from "./expert/ExpertCheckout";
import ExpertProfile from "./expert/ExpertProfile";
import ExpertAchievements from "./expert/ExpertAchievements";
import ExpertResources from "./expert/ExpertResources";
import Availability from "./expert/Availability";

// Client Components
import ClientDashboard from "./client/ClientDashboard";
import ClientHome from "./client/ClientHome";
import ClientBookings from "./client/ClientBookings";
import ClientFindTherapist from "./client/ClientFindTherapist";
import ClientProfile from "./client/ClientProfile";
import ClientResources from "./client/ClientResources";
import ClientWhatsNew from "./client/ClientWhatsNew";
import ClientReviewTherapist from "./client/ClientReviewTherapist";

// Test Therapist Components
import TestTherapistDashboard from "./therapist/test-therapist/TestDashboard";

// Test Client Components
import TestClientDashboard from "./test-client/TestDashboard";

// 🔐 Role-Based Protected Route Wrapper
const ProtectedRouteByRole = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user) {
    toast.warn("You must be logged in to access this page");
    // ✅ FIX: Redirect to home instead of rendering another HomePage
    return <Navigate to="/" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    toast.warn("You do not have permission to access this page");
    // Redirect to appropriate dashboard based on role
    return user.role === "client" ? <Navigate to="/client" /> : <Navigate to="/expert" />;
  }

  return children;
};

// ✅ Handles Root Redirection
const Root = () => {
  const { user } = useAuth();

  if (user) {
    if (user.role === "therapist") {
      return <Navigate to="/therapist/test-dashboard" />;
    }
    if (user.role === "expert") {
      return <Navigate to="/expert" />;
    }
    if (user.role === "client") {
      return <Navigate to="/client/test-dashboard" />;
    }
  }

  // ✅ If no user, show only one HomePage (no duplication)
  return <HomePage />;
};

// ✅ Main Routing Component
const Main = () => {
  const { accessToken: token } = useAuth();

  const handleBookingSubmit = async (payload) => {
    try {
      await axios.post("https://quirkleai.com/api/sessions/", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Booking created successfully!");
      window.location.reload(); // You can optimize this later
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error("Failed to create booking.");
    }
  };

  return (
    <Router>
      <ToastContainer position="top-center" autoClose={3000} />
      <Routes>
        <Route path="/" element={<Root />} />

        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/contact" element={<Contact />} />


        <Route path="/book/:linkId" element={<PublicBookingPage />} />
        <Route path="/book-session/:linkId" element={<BookSessionPage />} />

        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/therapist/test-dashboard" element={<TestTherapistDashboard />} />
        <Route path="/client/test-dashboard" element={<TestClientDashboard />} />

        {/* Expert Routes */}
        <Route
          path="/expert"
          element={
            <ProtectedRouteByRole allowedRoles={["expert", "therapist"]}>
              <ExpertDashboard />
            </ProtectedRouteByRole>
          }
        >
          <Route index element={<ExpertHome />} />
          <Route path="bookings" element={<ExpertBookings />} />
          <Route path="messages" element={<DirectMessages />} />
          <Route path="testimonials" element={<ExpertTestimonials />} />
          <Route path="calendar" element={<ExpertCalendar />} />
          <Route path="services" element={<ExpertServices />} />
          <Route path="analytics" element={<ExpertAnalytics />} />
          <Route path="availability" element={<Availability />} />
          <Route path="marketing" element={<ExpertMarketing />} />
          <Route path="payments" element={<ExpertPayments />} />
          <Route path="checkout" element={<ExpertCheckout />} />
          <Route path="profile" element={<ExpertProfile />} />
          <Route path="achievements" element={<ExpertAchievements />} />
          <Route path="resources" element={<ExpertResources />} />
        </Route>

        {/* Jitsi Room Route for Therapists */}
        <Route
          path="/therapist/jitsi-room/:roomName"
          element={
            <ProtectedRouteByRole allowedRoles={["therapist"]}>
              <JitsiRoom />
            </ProtectedRouteByRole>
          }
        />

        {/* Client Routes */}
        <Route
          path="/client"
          element={
            <ProtectedRouteByRole allowedRoles={["client"]}>
              <ClientDashboard />
            </ProtectedRouteByRole>
          }
        >
          <Route index element={<ClientHome />} />
          <Route path="bookings" element={<ClientBookings />} />
          <Route
            path="find-therapist"
            element={<ClientFindTherapist onSubmit={handleBookingSubmit} />}
          />
          <Route path="profile" element={<ClientProfile />} />
          <Route path="resources" element={<ClientResources />} />
          <Route path="whats-new" element={<ClientWhatsNew />} />
          <Route path="messages" element={<DirectMessages />} />
          <Route path="review-therapist/:therapistId" element={<ClientReviewTherapist />} />
          <Route path="therapist-profile/:therapistId" element={<ClientProfile />} />
        </Route>

        {/* Jitsi Room Route - Accessible by both Client and Therapist */}
        <Route
          path="/client/jitsi-room/:roomName"
          element={
            <ProtectedRouteByRole allowedRoles={["client", "therapist"]}>
              <JitsiRoom />
            </ProtectedRouteByRole>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

const App = () => (
  <AuthProvider>
    <Main />
  </AuthProvider>
);

export default App;
